import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import api from "@/lib/api";
import { Save, CheckCircle2, Send, Loader2, X, Bell, Mail, MessageCircle, Palette, ArrowRight, PlugZap, QrCode, Eraser, AlertTriangle } from "lucide-react";

// i18n runs with escapeValue:false, so any value interpolated into a string
// rendered via dangerouslySetInnerHTML must be HTML-escaped here to avoid XSS.
const escapeHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (ch) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
  ));

export default function Settings() {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation("common");
  const { t: twa } = useTranslation("whatsapp");
  const { user } = useAuth();
  const { org, refresh: refreshOrg } = useOrg();
  const [branding, setBranding] = useState({ name: "", primary_color: "#0a0a0a", logo_url: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);

  useEffect(() => {
    if (org) setBranding({
      name: org.name || "",
      primary_color: org.primary_color || "#0a0a0a",
      logo_url: org.logo_url || "",
    });
  }, [org]);

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setSavingBrand(true);
    try {
      await api.put("/organizations/me", branding);
      await refreshOrg();
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 2500);
    } finally {
      setSavingBrand(false);
    }
  };
  const [form, setForm] = useState({
    default_vat_rate: 23,
    company_name: "",
    company_nif: "",
    alert_emails: [],
    alert_whatsapp_numbers: [],
    alerts_promoter_confirmed: true,
    alerts_event_filled: true,
    alerts_invoice_overdue: true,
    alerts_promoter_reminder: true,
    alerts_auto_resend_24h: true,
    invoice_overdue_days: 30,
    providers: { meta_cloud: {
      status: "pending",
      waba_id: "",
      phone_number_id: "",
      business_account_id: "",
      app_id: "",
      app_secret: "",
      access_token: "",
      webhook_verify_token: "",
      default_language: "pt_PT",
    } },
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testStatus, setTestStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newWa, setNewWa] = useState("");
  const [whatsappHealth, setWhatsappHealth] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setForm((f) => ({ ...f, ...r.data }));
      setLoaded(true);
    });
    api.get("/organizations/me/whatsapp/health")
      .then((r) => setWhatsappHealth(r.data))
      .catch(() => setWhatsappHealth(null));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    // Meta Cloud secrets: strip masked placeholders so backend keeps stored values
    if (payload.providers?.meta_cloud) {
      const m = { ...payload.providers.meta_cloud };
      ["access_token", "app_secret", "webhook_verify_token"].forEach((k) => {
        if (m[k] && m[k].includes("…")) m[k] = null;
      });
      payload.providers = { ...payload.providers, meta_cloud: m };
    }
    try {
      const { data } = await api.put("/settings", payload);
      setForm((f) => ({ ...f, ...data, providers: data.providers || f.providers }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  // Meta test config action moved to superadmin UI (Session A+).

  const handleTest = async () => {
    if (!testPhone) return;
    setTesting(true);
    setTestStatus(null);
    try {
      const { data } = await api.post("/settings/test-whatsapp", { phone: testPhone });
      setTestStatus(data);
    } catch (e) {
      setTestStatus({ ok: false, detail: e.response?.data?.detail || "Erro" });
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return <div className="text-sm text-zinc-500">{tc("states.loading")}</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
      </div>

      <section className="bg-white border border-zinc-200 rounded-xl p-6 space-y-3">
        <h2 className="text-lg font-display font-semibold text-zinc-900">{tc("labels.name")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <Info label={tc("labels.name")} value={user?.name} />
          <Info label={tc("labels.email")} value={user?.email} />
        </div>
      </section>

      <form onSubmit={handleSaveBranding} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4" data-testid="org-branding-section">
        <div>
          <h2 className="text-lg font-display font-semibold text-zinc-900 flex items-center gap-2"><Palette className="w-4 h-4" /> {t("sections.branding")}</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{t("sections.brandingDesc")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("form.name")}>
            <input data-testid="brand-name-input" value={branding.name} onChange={(e) => setBranding({ ...branding, name: e.target.value })} className="form-input" />
          </Field>
          <Field label={t("form.primaryColor")}>
            <input data-testid="brand-color-input" type="color" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="form-input h-10 p-1" />
          </Field>
          <Field label={t("form.logo")}>
            <input data-testid="brand-logo-input" type="url" value={branding.logo_url || ""} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} className="form-input" placeholder="https://…/logo.png" />
          </Field>
          <div className="flex items-center gap-3 pt-6">
            <div data-testid="brand-preview" className="w-10 h-10 rounded-md flex items-center justify-center text-white text-sm font-semibold" style={{ background: branding.primary_color }}>
              {branding.logo_url ? <img src={branding.logo_url} alt="" className="w-full h-full object-cover rounded-md" /> : (branding.name || "P").slice(0, 1)}
            </div>
            <div className="text-xs text-zinc-500">Preview</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="save-branding-btn" type="submit" disabled={savingBrand} className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {savingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {savingBrand ? t("form.saving") : t("form.save")}
          </button>
          {brandSaved && (
            <span data-testid="brand-saved" className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("form.saved")}</span>
          )}
        </div>
      </form>

      <form onSubmit={handleSave} className="space-y-6">
        <section className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-display font-semibold text-zinc-900">{t("sections.companyInfo")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={tc("labels.company")}>
              <input data-testid="company-name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="form-input" />
            </Field>
            <Field label={t("form.companyNif")}>
              <input data-testid="company-nif" value={form.company_nif || ""} onChange={(e) => setForm({ ...form, company_nif: e.target.value })} className="form-input" />
            </Field>
            <Field label="IVA (%)">
              <input data-testid="vat-rate" type="number" step="0.5" value={form.default_vat_rate} onChange={(e) => setForm({ ...form, default_vat_rate: parseFloat(e.target.value) || 0 })} className="form-input" />
            </Field>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4" data-testid="whatsapp-provider-card">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-display font-semibold text-zinc-900 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" /> {t("sections.provider")}
              </h2>
              <p className="text-sm text-zinc-500 mt-0.5">{t("sections.providerDesc")}</p>
            </div>
            <ProviderBadge health={whatsappHealth} twa={twa} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
            <MiniStat label={tc("labels.type")} value={providerLabel(whatsappHealth?.whatsapp_provider || whatsappHealth?.provider, twa)} />
            <MiniStat label={t("provider.state")} value={twa(`state.${whatsappHealth?.active_status || whatsappHealth?.status || "not_configured"}`)} />
            <MiniStat label={t("provider.phone")} value={whatsappHealth?.display_phone_number || whatsappHealth?.z_api?.phone || "—"} mono />
          </div>

          <div className="pt-2 border-t border-zinc-100 flex flex-wrap gap-2 items-center">
            <Link
              to="whatsapp"
              data-testid="open-whatsapp-page"
              className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold"
            >
              {(whatsappHealth?.active_status || whatsappHealth?.status) === "connected"
                ? (<><PlugZap className="w-4 h-4" /> {t("provider.manage")}</>)
                : (whatsappHealth?.whatsapp_provider || whatsappHealth?.provider) === "z_api"
                  ? (<><QrCode className="w-4 h-4" /> {t("provider.connectQr")}</>)
                  : (<><PlugZap className="w-4 h-4" /> {t("provider.connect")}</>)}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="save-settings" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" /> {t("form.save")}
          </button>
          {saved && <span className="text-sm text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {t("form.saved")}</span>}
        </div>

        <section className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5" data-testid="alerts-section">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-zinc-700" />
            <h2 className="text-lg font-display font-semibold text-zinc-900">Alertas</h2>
          </div>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="manager-alerts-disabled-notice">
            Os alertas para gestores (email e WhatsApp: promotor confirmado, evento preenchido, recibo em atraso) estão <strong>desativados</strong> de momento. As notificações dentro da app continuam ativas.
          </p>
          <fieldset disabled className="space-y-5 opacity-50 pointer-events-none" data-testid="manager-alerts-fieldset">

          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Emails de alerta</label>
            <div className="flex flex-wrap gap-2">
              {(form.alert_emails || []).map((em) => (
                <span key={em} data-testid={`email-chip-${em}`} className="inline-flex items-center gap-1.5 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 text-xs">
                  {em}
                  <button type="button" onClick={() => setForm({ ...form, alert_emails: form.alert_emails.filter((x) => x !== em) })} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="new-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@example.com" className="form-input flex-1" />
              <button type="button" data-testid="add-email-btn" onClick={() => { if (newEmail && !form.alert_emails.includes(newEmail)) { setForm({ ...form, alert_emails: [...form.alert_emails, newEmail] }); setNewEmail(""); } }} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">{tc("actions.add")}</button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp de alerta</label>
            <div className="flex flex-wrap gap-2">
              {(form.alert_whatsapp_numbers || []).map((wa) => (
                <span key={wa} data-testid={`wa-chip-${wa}`} className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-xs text-emerald-900">
                  {wa}
                  <button type="button" onClick={() => setForm({ ...form, alert_whatsapp_numbers: form.alert_whatsapp_numbers.filter((x) => x !== wa) })} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="new-wa-input" value={newWa} onChange={(e) => setNewWa(e.target.value)} placeholder="351912345678" className="form-input flex-1" />
              <button type="button" data-testid="add-wa-btn" onClick={() => { if (newWa && !form.alert_whatsapp_numbers.includes(newWa)) { setForm({ ...form, alert_whatsapp_numbers: [...form.alert_whatsapp_numbers, newWa] }); setNewWa(""); } }} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">{tc("actions.add")}</button>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-zinc-100">
            <AlertToggle label="Promotor confirmado" testid="toggle-promoter-confirmed" checked={form.alerts_promoter_confirmed} onChange={(v) => setForm({ ...form, alerts_promoter_confirmed: v })} />
            <AlertToggle label="Evento preenchido" testid="toggle-event-filled" checked={form.alerts_event_filled} onChange={(v) => setForm({ ...form, alerts_event_filled: v })} />
            <AlertToggle label="Recibo em atraso" testid="toggle-invoice-overdue" checked={form.alerts_invoice_overdue} onChange={(v) => setForm({ ...form, alerts_invoice_overdue: v })} />
          </div>
          </fieldset>
          <div className="space-y-2 pt-2 border-t border-zinc-100">
            <AlertToggle label="Lembrete da véspera aos confirmados (template lembrete_evento_24h, 12–24h antes)" testid="toggle-promoter-reminder" checked={form.alerts_promoter_reminder} onChange={(v) => setForm({ ...form, alerts_promoter_reminder: v })} />
            <AlertToggle label="Reenvio automático a quem não responde (24h)" testid="toggle-auto-resend-24h" checked={form.alerts_auto_resend_24h} onChange={(v) => setForm({ ...form, alerts_auto_resend_24h: v })} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-zinc-100">
            <Field label="Dias até recibo em atraso">
              <input data-testid="overdue-days" type="number" min="1" value={form.invoice_overdue_days} onChange={(e) => setForm({ ...form, invoice_overdue_days: parseInt(e.target.value) || 30 })} className="form-input" />
            </Field>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="save-alerts" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" /> {t("form.save")}
          </button>
        </div>
      </form>

      <section className="bg-white border border-zinc-200 rounded-xl p-6 space-y-3">
        <h2 className="text-lg font-display font-semibold text-zinc-900">{twa("test.title")}</h2>
        <p className="text-sm text-zinc-500">{twa("test.help")}</p>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder={twa("test.toPlaceholder")} className="form-input flex-1 min-w-[200px]" />
          <button onClick={handleTest} disabled={!testPhone || testing} data-testid="test-whatsapp-button" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {twa("test.send")}
          </button>
        </div>
        {testStatus && (
          <div data-testid="test-result" className={`text-sm rounded-lg px-3 py-2 border ${testStatus.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
            {testStatus.ok ? `✓ ${twa("test.success")}` : `✗ ${testStatus.detail}`}
          </div>
        )}
      </section>

      {user?.role === "admin" && <DangerZone slug={org?.slug} />}

      <style>{`.form-input{width:100%;padding:0.625rem 0.75rem;border-radius:0.5rem;border:1px solid #e4e4e7;font-size:0.875rem;background:#fff} .form-input:focus{outline:none;border-color:#09090b;box-shadow:0 0 0 2px #09090b}`}</style>
    </div>
  );
}


function DangerZone({ slug }) {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation("common");
  const [modalOpen, setModalOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const canConfirm = typed.trim() === slug;

  const submit = async (e) => {
    e.preventDefault();
    if (!canConfirm) return;
    setSaving(true); setError("");
    try {
      const r = await api.post("/organizations/me/reset-data", { confirm_slug: slug });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || tc("errors.generic"));
    } finally { setSaving(false); }
  };

  const close = () => { setModalOpen(false); setTyped(""); setError(""); setResult(null); };

  return (
    <section className="bg-white border border-red-200 rounded-xl p-6 space-y-3" data-testid="danger-zone-section">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <h2 className="text-lg font-display font-semibold text-red-800">{t("sections.danger")}</h2>
      </div>
      <p className="text-sm text-zinc-600 leading-relaxed">
        {t("sections.dangerDesc")} {t("danger.willPreserve")}
      </p>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="open-reset-account-modal"
        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
      >
        <Eraser className="w-4 h-4" /> {t("danger.openReset")}
      </button>

      {modalOpen && (
        <div className="fixed inset-0 bg-zinc-950/70 z-50 flex items-center justify-center p-4" data-testid="reset-account-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
            <button onClick={close} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-800">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Eraser className="w-5 h-5 text-red-700" />
              <h3 className="text-lg font-display font-semibold text-zinc-950">{t("danger.modalTitle")}</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">{t("danger.warningIntro")}</p>
            <ul className="text-xs text-zinc-600 space-y-1 mb-4 list-disc list-inside">
              <li>{t("danger.willDelete.events")}</li>
              <li>{t("danger.willDelete.promoters")}</li>
              <li>{t("danger.willDelete.clients")}</li>
              <li>{t("danger.willDelete.invoices")}</li>
              <li>{t("danger.willDelete.templates")}</li>
              <li>{t("danger.willDelete.notifications")}</li>
              <li>{t("danger.willDelete.logs")}</li>
            </ul>
            <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-4">{t("danger.willPreserve")}</p>

            {!result ? (
              <form onSubmit={submit} className="space-y-4">
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>{tc("confirm.irreversible")}</div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                    <span dangerouslySetInnerHTML={{ __html: t("danger.confirmLabel", { slug: escapeHtml(slug) }) }} />
                  </label>
                  <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    data-testid="reset-account-confirm-input"
                    placeholder={t("danger.confirmPlaceholder")}
                    className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    autoFocus
                  />
                </div>
                {error && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                <div className="flex gap-2">
                  <button type="button" onClick={close}
                          className="flex-1 border border-zinc-300 text-zinc-800 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold">
                    {t("danger.cancel")}
                  </button>
                  <button type="submit" disabled={!canConfirm || saving} data-testid="reset-account-submit"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />}
                    {saving ? t("danger.submitting") : t("danger.submit")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {t("danger.resultTitle")}
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">{t("danger.resultCounts")}</div>
                <div className="text-xs text-zinc-600 max-h-52 overflow-auto border border-zinc-200 rounded-lg p-3 font-mono">
                  {Object.entries(result.deleted || {}).map(([k, v]) => (
                    v > 0 ? <div key={k}>{k.padEnd(25, " ")} <span className="text-red-700">{v}</span></div> : null
                  ))}
                </div>
                <button onClick={() => { close(); window.location.reload(); }}
                        className="w-full bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
                  {t("danger.close")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function AlertToggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer" data-testid={testid}>
      <span className="text-sm text-zinc-700">{label}</span>
      <button type="button" onClick={() => onChange(!checked)} className={`relative w-9 h-5 rounded-full transition-colors ${checked ? "bg-zinc-950" : "bg-zinc-200"}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </button>
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm text-zinc-900 mt-1">{value || "—"}</div>
    </div>
  );
}


function ProviderBadge({ health, twa }) {
  if (!health) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-zinc-100 text-zinc-600 border-zinc-200">
        …
      </span>
    );
  }
  const activeStatus = health.active_status || health.status;
  const clsMap = {
    connected:          "bg-emerald-100 text-emerald-800 border-emerald-200",
    not_connected:      "bg-zinc-100 text-zinc-700 border-zinc-200",
    disconnected:       "bg-zinc-100 text-zinc-700 border-zinc-200",
    not_configured:     "bg-zinc-100 text-zinc-700 border-zinc-200",
    attention_required: "bg-amber-100 text-amber-900 border-amber-200",
    reconnect_required: "bg-red-100 text-red-800 border-red-200",
  };
  const cls = clsMap[activeStatus] || clsMap.not_connected;
  return (
    <span data-testid="wa-provider-status-badge"
          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {twa(`state.${activeStatus}`, activeStatus)}
    </span>
  );
}


function MiniStat({ label, value, mono }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-medium text-zinc-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}


function providerLabel(p, twa) {
  return {
    meta_cloud: twa("providers.meta_cloud"),
    twilio:     twa("providers.twilio"),
    z_api:      twa("providers.z_api"),
  }[p] || twa("providers.meta_cloud");
}
